import { useRef, useState } from "react";
import { Move } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { imageUrl } from "@/lib/api";

export const focusStyle = (focus) => (focus
  ? { objectPosition: `${focus.x}% ${focus.y}%`, transform: `scale(${focus.zoom})`, transformOrigin: `${focus.x}% ${focus.y}%` }
  : undefined);

const SHAPES = { circle: { w: 260, h: 260, cls: "rounded-full" }, wide: { w: 400, h: 240, cls: "rounded-xl" } };
const clamp = (v) => Math.min(100, Math.max(0, v));

export const PhotoFocusEditor = ({ path, focus, open, onOpenChange, onSave, title = "Adjust your profile picture", shape = "circle", hint = "Drag the photo to position it inside the circle, and zoom to frame it." }) => {
  const S = SHAPES[shape];
  const [f, setF] = useState(focus?.path === path ? focus : { path, x: 50, y: 50, zoom: 1 });
  const drag = useRef(null);
  const onDown = (e) => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { sx: e.clientX, sy: e.clientY, x: f.x, y: f.y }; };
  const onMove = (e) => {
    if (!drag.current) return;
    const k = 100 / (S.w * f.zoom);
    setF((p) => ({ ...p, x: clamp(drag.current.x - (e.clientX - drag.current.sx) * k), y: clamp(drag.current.y - (e.clientY - drag.current.sy) * k) }));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${shape === "wide" ? "max-w-lg" : "max-w-sm"} border-border bg-card`} data-testid="photo-focus-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          <DialogDescription>{hint}</DialogDescription>
        </DialogHeader>
        <div className={`mx-auto mt-2 max-w-full touch-none select-none overflow-hidden border-2 border-primary/60 bg-secondary ${S.cls}`}
          style={{ width: S.w, height: S.h, cursor: drag.current ? "grabbing" : "grab" }}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}
          data-testid="photo-focus-area">
          <img src={imageUrl(path)} alt="Photo being framed" draggable={false} className="img-cover pointer-events-none" style={focusStyle(f)} />
        </div>
        <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground"><Move className="h-3.5 w-3.5" /> Drag to move</p>
        <div className="space-y-2">
          <label className="field-label" htmlFor="focus-zoom">Zoom</label>
          <Slider id="focus-zoom" min={1} max={3} step={0.05} value={[f.zoom]} onValueChange={([z]) => setF((p) => ({ ...p, zoom: z }))} data-testid="photo-focus-zoom" />
        </div>
        <div className="mt-2 flex justify-between gap-2">
          <button type="button" className="btn-link text-sm" onClick={() => setF({ path, x: 50, y: 50, zoom: 1 })} data-testid="photo-focus-reset">Reset</button>
          <div className="flex gap-2">
            <button type="button" className="btn-outline h-11 px-4 text-sm" onClick={() => onOpenChange(false)} data-testid="photo-focus-cancel">Cancel</button>
            <button type="button" className="btn-primary h-11 px-5 text-sm" onClick={() => { onSave({ ...f, x: Math.round(f.x * 10) / 10, y: Math.round(f.y * 10) / 10 }); onOpenChange(false); }} data-testid="photo-focus-save">Done</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
